import { useCallback, useEffect, useState } from "react";

import { themeCookieChange } from "@/global/layout-client/actions/theme";
import { SelectableTheme, Theme } from "@/shared/types/theme";

const getMatcher = () => window?.matchMedia("(prefers-color-scheme: dark)");

export const useTheme = () => {
  const [themeMode, setThemeMode] = useState<Theme>("light");

  const [selectableTheme, setSelectableTheme] =
    useState<SelectableTheme>("auto");

  const setThemeColor = useCallback((theme: Theme) => {
    setThemeMode(theme);
    document.documentElement.setAttribute("data-theme", theme);
    themeCookieChange(theme);
  }, []);

  const changeThemeOption = useCallback(
    (theme: SelectableTheme) => {
      localStorage.setItem("theme", theme);
      setSelectableTheme(theme);
      if (theme === "light" || theme === "dark") {
        return setThemeColor(theme);
      }
      const isBrowserDarkTheme = getMatcher().matches;
      setThemeColor(isBrowserDarkTheme ? "dark" : "light");
    },
    [setThemeColor],
  );

  useEffect(() => {
    const theme = (localStorage.getItem("theme") || "auto") as SelectableTheme;
    changeThemeOption(theme);

    if (selectableTheme !== "auto") {
      return;
    }
    const matcher = getMatcher();
    const listener = (e: MediaQueryListEvent) => {
      setThemeColor(e.matches ? "dark" : "light");
    };

    matcher.addEventListener("change", listener);
    return () => {
      matcher.removeEventListener("change", listener);
    };
  }, [setThemeColor, selectableTheme, changeThemeOption]);

  return {
    changeTheme: changeThemeOption,
    themeMode,
    selectableTheme,
  };
};
